import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import SnTab from "@shared/components/SnTab";
import { menuInfo } from "@shared/type/CommonType";
import SnNavBtn from "@shared/components/SnNavBtn";
import SnDndFormControls from "@shared/components/SnDndFormControls";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { global } from "@shared/libs/Global";
import DrawingButtons from "./DesignerButtons";
import { getTablebySql } from "@shared/utils/CommonUtils";
import SnButton from "@shared/components/SnButton";
import { showResizablePopupModal } from "@widgets/CommonModal";
import TemplatePopup1 from "./Templates/TemplatePopup1";

interface DesignerFreeFormProps {
  type: string;
  data: any;
  controls: any[];
  onButtonClick?: (sender: string) => void;
  title?: string;
  header?: React.ReactNode;
  menuInfo?: menuInfo;
  settingsData?: any;
}

type InputEls = {
  el: HTMLElement | null;
  isFocusable: boolean;
};

const DesignerFreeForm = forwardRef(
  (
    {
      type,
      data,
      controls,
      onButtonClick,
      title,
      header,
      menuInfo,
      settingsData,
    }: DesignerFreeFormProps,
    ref
  ) => {
    const [formData, setFormData] = useState<any>(data);
    const [visibleControls, setVisibleControls] = useState<any[]>([]);
    const [hiddenControls, setHiddenControls] = useState<any[]>([]);
    const [editCons, setEditCons] = useState<any[]>();
    const [editCon, setEditCon] = useState<any>();
    const [requireds, setRequireds] = useState<any>({});
    const [groups, setGroups] = useState<any[]>();
    const [filters, setFilters] = useState<any[]>();
    const [filter, setFilter] = useState<any>();
    const formRef = useRef<HTMLDivElement>(null); // form 영역을 참조
    const [menuPosition, setMenuPosition] = useState<{
      mouseX: any;
      mouseY: any;
    } | null>(null);
    const refs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    // const inputEls = useRef<HTMLInputElement[]>([]); // DesignerFreeForm에 있는 input element 모음
    const inputEls = useRef<InputEls[]>([]);
    const [scrollTop, setScrollTop] = useState(0);
    const [popupCons, setPopupCons] = useState<any>({}); /* */
    const { userInfo } = global();

    const [settings, setSettings] = useState({
      title: "",
      table: "",
      defaultValue: "",
      button: "",
      defaultValueObject: null,
      buttonNode: null,
      columnsInfo: null,
    });
    const [popupSettings, setPopupSettings] = useState<any>();

    const fetchControl = async () => {
      try {
        const eidtConFilters = controls.filter(
          (control) =>
            control.dc_editcon &&
            control.dc_editcon.length > 0 &&
            control.dc_editcon.trim() != ""
        );
        setEditCons(eidtConFilters);

        const filterFilters = controls.filter(
          (control) =>
            control.dc_filter &&
            control.dc_filter.length > 0 &&
            control.dc_filter.trim() != ""
        );
        setFilters(filterFilters);

        const groupFilters = controls.filter(
          (control) => control.tp_ctrl === "SnGroupLabel"
        );
        setGroups(groupFilters);

        const requiredsFilter = controls.filter(
          (control) => control.yn_required === "Y"
        );
        let tempreq = Object.assign({});
        requiredsFilter.map((req) => {
          tempreq[req.cd_param] = req.nm_label;
        });
        setRequireds(tempreq);
        const visibleFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() == "Y"
        );
        setVisibleControls(visibleFilters);
        const hiddenFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() != "Y"
        );
        setHiddenControls(hiddenFilters);
      } catch (error: any) {}
    };

    const fetchData = async () => {
      try {
        const idleId = window.requestIdleCallback(() => {
          setChangeCon(data);
          setFormData(data); // 상태 갱신 (폼 렌더링 유발)
        });

        return () => {
          cancelIdleCallback(idleId);
        };
      } catch (error: any) {}
    };

    // useEffect(() => {
    //   fetchData();
    //   setTimeout(() => {
    //     requestAnimationFrame(() => {
    //       focusInputByIndex(0);
    //     });
    //   }, 0);
    // }, [data]);

    useEffect(() => {
      if (settingsData) {
        let button;
        if (settingsData.button) {
          button = convertButton(settingsData.button); // 버튼 변환
        }
        setSettings({
          ...settingsData,
          buttonNode: button,
        });
      }
    }, [settingsData]);

    useEffect(() => {
      fetchControl();
    }, [controls]);

    useEffect(() => {
      if (groups) {
        const first = groups[0]?.no_line;
        if (first) {
          groupButtonClick(`${first}`);
        }
      }
    }, [groups, refs]);

    const setChangeCon = useCallback(
      (change: any) => {
        const editObj: Record<string, any> = {};
        const filterObj: Record<string, any> = {};
        const visibleObj: Record<string, boolean> = {};

        // editCon 처리
        if (editCons && change) {
          editCons.forEach((con) => {
            let conData = con.dc_editcon;

            if (change.state === "insert") {
              conData = conData.replace("new", "false");
            } else {
              conData = conData.replace("new", "true");
            }

            Object.keys(change).forEach((key) => {
              conData = conData.replaceAll(key, change[key]);
            });

            editObj[con.cd_param] = conData;
          });
          setEditCon(editObj);
        }

        // filter 처리
        if (filters && change) {
          filters.forEach((fil) => {
            let filData = fil.dc_filter;
            Object.keys(change).forEach((key) => {
              filData = filData.replaceAll(`{${key}}`, change[key]);
            });
            filterObj[fil.cd_param] = filData;
          });
          setFilter(filterObj);
        }

        // visible 처리
        if (visibleControls) {
          visibleControls.forEach((control) => {
            const raw = editObj?.[control.cd_param];
            if (raw && change.hasOwnProperty(control.cd_param)) {
              try {
                visibleObj[control.cd_param] = Boolean(
                  new Function(`return ${raw};`)()
                );
              } catch (e) {
                visibleObj[control.cd_param] = false;
                console.error("eval error in setChangeCon:", raw, e);
              }
            } else {
              visibleObj[control.cd_param] = false;
            }
          });
        }

        // 필요시 visibleObj 저장 또는 상태 처리 가능
      },
      [editCons, filters, visibleControls]
    );

    const setReadOnly = useCallback(
      (param: string) => {
        const expr = editCon?.[param];
        if (!expr) return false;

        try {
          return Boolean(new Function(`return (${expr});`)());
        } catch (e) {
          console.error("eval error in setReadOnly:", expr, e);
          return false;
        }
      },
      [editCon]
    );

    useImperativeHandle(ref, () => ({
      focusInputByIndex,
      applyPopupConditions: (popupConditions: any) =>
        setPopupCons(popupConditions),
      getControls: async () => {
        return visibleControls;
      },
      getFormData: async () => {
        return formData;
      },
      getSettings: async () => {
        let defaultValue: any = {};
        if (
          visibleControls.length === 1 &&
          Object.keys(visibleControls[0]).length === 0
        )
          return { ...settings, defaultValue: {} };
        const filtered = visibleControls.filter(
          (col) =>
            col.defaultValue !== undefined &&
            col.defaultValue !== null &&
            col.defaultValue !== ""
        );
        filtered.map((col) => {
          if (col.tp_column === "SnNumberBox")
            defaultValue[col.cd_param] = col.defaultValue;
          else defaultValue[col.cd_param] = `'${col.defaultValue}'`;
        });
        const filteredDt = visibleControls.filter((col) =>
          col.cd_param.includes("dt_")
        );
        filteredDt.map((col) => {
          if (
            col.defaultValue !== undefined &&
            col.defaultValue !== null &&
            col.defaultValue !== ""
          )
            defaultValue[col.cd_param] =
              `getConvertedDate('${col.defaultValue}')`;
          else defaultValue[col.cd_param] = `getConvertedDate('0/0/0')`;
        });

        let columnsInfo;
        if (settings.columnsInfo == null) {
          columnsInfo = await getColumnsInfo(settings.table);
        } else {
          columnsInfo = settings.columnsInfo;
        }

        columnsInfo?.map((col) => {
          Object.keys(userInfo).forEach((key) => {
            if (col.column_name.includes(key) && key !== "cd_cust") {
              defaultValue[col.column_name] = `userInfo.${key}`;
              if (key in ["cd_emp", "cd_dept", "cd_gp", "cd_user"]) {
                defaultValue[col.column_name.replace("cd", "nm")] =
                  `userInfo.${key.replace("cd", "nm")}`;
              }
            }
          });
        });
        return {
          ...settings,
          defaultValue: defaultValue,
          columnsInfo: columnsInfo,
        };
      },
    }));

    const getColumnsInfo = async (tablename: string) => {
      try {
        const rtn: any[] = await getTablebySql({
          sql: `SELECT
                  col.column_name,
                  col.data_type,
                  col.character_maximum_length,
                  col.numeric_precision,
                  col.numeric_scale,
                  col.is_nullable,
                  col.column_default,
                  CASE WHEN pk.column_name IS NOT NULL THEN 'YES' ELSE 'NO' END AS is_primary_key
              FROM information_schema.columns col
              LEFT JOIN (
                  SELECT 
                      kcu.column_name
                  FROM 
                      information_schema.table_constraints tc
                  JOIN information_schema.key_column_usage kcu 
                      ON tc.constraint_name = kcu.constraint_name
                      AND tc.table_name = kcu.table_name
                  WHERE tc.constraint_type = 'PRIMARY KEY'
                    AND tc.table_name = '${tablename}'
                    AND tc.table_schema = 'snotes'
              ) pk
                  ON col.column_name = pk.column_name
              WHERE col.table_name = '${tablename}'
                AND col.table_schema = 'snotes'
              ORDER BY col.ordinal_position;`,
        });
        return rtn;
      } catch (error) {}
    };

    const gridContainerStyle = {
      display: "grid",
      gridTemplateColumns: "repeat(13, minmax(50px, 1fr))",
      gridAutoRows: "32px",
      rowGap: "10px",
      columnGap: "15px",
      padding: "10px",
      overflowY: "auto" as "auto",
      flex: "1 1 auto",
      // border: "1px solid rgb(186,191,199)",
      position: "relative",
    } as const;

    const moveControl = (fromIdx: number, toIdx: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(fromIdx, 1); // 1. 기존 위치에서 제거
        updated.splice(toIdx, 0, moved); // 2. 목표 인덱스에 삽입
        return updated;
      });
    };

    const removeControl = (Idx: number, Id: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        updated.splice(Idx, 1); // 1. 기존 위치에서 제거
        return updated;
      });

      // controls에서 no_line 값이 Id와 같은 control을 찾아 hiddenControls에 추가
      const controlToHide = controls.find((control) => control.no_line === Id);
      if (controlToHide) {
        setHiddenControls((prev) => [...prev, controlToHide]);
      }
    };

    const groupButtonClick = (index: any) => {
      const target = refs.current[index];
      if (target && formRef.current) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    };

    const setRef = (key: any, node: HTMLDivElement | null) => {
      if (node) {
        refs.current[key] = node; // key를 기준으로 DOM 요소를 저장
      }
    };

    const setInputRef = (
      el: HTMLElement | null,
      index: number,
      isReadOnly: boolean
    ) => {
      inputEls.current[index] = {
        el,
        isFocusable: !isReadOnly,
      };
    };

    const focusInputByIndex = async (startIndex: number) => {
      let index = startIndex;

      while (index < inputEls.current.length) {
        const input = inputEls.current[index]?.el;
        const isFocusable = inputEls.current[index]?.isFocusable;
        if (input && isFocusable) {
          input.focus();
          break;
        }
        index++;
      }
    };

    const onGridSetting = (value: any) => {
      setSettings(value);
    };

    const convertButton = (value: string) => {
      const buttonLabels = value
        .split(",")
        .map((label) => label.trim())
        .filter((label) => label);

      const nodes = buttonLabels.map((label) => (
        <SnButton
          key={label}
          text={label}
          onClick={() => onGridBtnClick(label)}
        />
      ));
      return nodes;
    };

    const onGridBtnClick = async (key: string) => {
      if (key.includes("적용")) {
        const result = await showResizablePopupModal({
          title: `적용`,
          children: <TemplatePopup1 loadData={null} />,
        });
        if (!result || result === "close") return;
        setPopupSettings((prev: any = {}) => ({
          ...prev,
          [key]: result[0],
        }));
      } else {
        alert(key);
      }
    };

    const renderSharedContent = () => {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            border: "1px solid rgb(186,191,199)",
          }}
        >
          <div className={"flex justify-start"}>
            {groups != null &&
              groups.length > 0 &&
              groups.map((group) => {
                return (
                  <div>
                    <SnNavBtn
                      text={group.nm_label}
                      onClick={() => groupButtonClick(group.no_line)}
                    ></SnNavBtn>
                  </div>
                );
              })}
          </div>

          <div style={gridContainerStyle} ref={formRef} className="h-[1px]">
            <DndProvider backend={HTML5Backend}>
              <>
                {visibleControls != null &&
                  visibleControls.length > 0 &&
                  visibleControls.map((control, index) => (
                    <SnDndFormControls
                      labletype="DesignerFreeForm"
                      control={control}
                      id={control.no_line}
                      index={index}
                      formData={formData}
                      requireds={requireds}
                      popupCons={popupCons}
                      setRef={setRef}
                      setInputRef={setInputRef}
                      handleMoveFocus={(
                        fromIndex: number,
                        target: string
                      ) => {}}
                      onTextValueChanged={(param: string, data: string) => {}}
                      onNumberValueChanged={(param: string, data: number) => {}}
                      onPeriodValueChanged={(
                        param: string,
                        start: string,
                        end: string
                      ) => {}}
                      onCodeValueChanged={(
                        code: string,
                        data: string,
                        name: string,
                        data2: string,
                        rowData?: any
                      ) => {}}
                      setReadOnly={setReadOnly}
                      onButtonClick={(param: string) => {
                        onButtonClick != null && onButtonClick(param);
                      }}
                      moveControl={moveControl}
                      removeControl={removeControl}
                      filter={filter}
                    />
                  ))}
              </>
            </DndProvider>
          </div>
          <div className="relative">
            <DrawingButtons
              type={type}
              data={visibleControls}
              onAdd={(value: any[]) => {
                setVisibleControls((prev) => {
                  const merged = [...prev, ...value[0]];
                  const sorted = merged.map((col, idx) => ({
                    ...col,
                    no_seq: idx + 1,
                    no_line: idx + 1,
                    cd_form: "freeform1",
                  })); // no_seq 재조정
                  return sorted;
                });
                setFormData((prev: any) => {
                  const newFormData = { ...prev };

                  value[0].forEach((item: any) => {
                    newFormData[item.cd_param] = item.cd_value;
                  });

                  return newFormData;
                });
              }}
              onChange={(value: any[]) => {
                setVisibleControls(value);

                setFormData((prev: any) => {
                  const newFormData = { ...prev };

                  value.forEach((item: any) => {
                    newFormData[item.cd_param] = item.cd_value;
                  });

                  return newFormData;
                });
              }}
              onGridSetting={onGridSetting}
              settings={settings}
            />
          </div>
        </div>
      );
    };

    return (
      <>
        {title ? (
          <SnTab
            titles={[title]}
            width={"100%"}
            header={header}
            body={[renderSharedContent()]}
          />
        ) : (
          renderSharedContent()
        )}
      </>
    );
  }
);
export default React.memo(DesignerFreeForm);
